import "./publicPath";
import React, { Suspense } from "react";
import ReactDOM from "react-dom";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import { Provider } from "react-redux";
import { store, persistor } from "./Store";
import { PersistGate } from "redux-persist/integration/react";
import { BrowserRouter } from "react-router-dom";
import PageLoader from "./components/Loader/PageLoader";
import MyProvider from "./components/Layout/CommonProvider";
import { initializeI18n } from "./Utils/Translate";
import { HelmetProvider } from 'react-helmet-async';
import { setupGlobalChunkErrorHandler } from "./Utils/chunkRetryUtils";

// Global Error Handlers
// Handle global JavaScript errors
window.onerror = function (message, source, lineno, colno, error) {
  if (process.env.NODE_ENV === 'development') {
    console.error("Global JS Error:", {
      message,
      source,
      lineno,
      colno,
      error
    });
  }

  // Log to error service in production (optional)
  if (process.env.NODE_ENV === 'production') {
    // Silent logging - don't break the app
    try {
      // You can send to your error logging service here
      // fetch('/api/log-error', { ... })
    } catch (e) { }
  }

  return true; // Prevents default browser error popup
};

// Handle unhandled promise rejections
window.addEventListener('unhandledrejection', function (event) {
  if (process.env.NODE_ENV === 'development') {
    console.error("Unhandled Promise Rejection:", event.reason);
  }

  // Log to error service in production (optional)
  if (process.env.NODE_ENV === 'production') {
    try {
      // You can send to your error logging service here
      // fetch('/api/log-error', { ... })
    } catch (e) { }
  }

  event.preventDefault(); // Prevents console error logs
});

setupGlobalChunkErrorHandler();

const clearStaleBrowserCaches = async () => {
  try {
    if (!("caches" in window)) {
      return;
    }

    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map((cacheName) => caches.delete(cacheName)));
  } catch (error) {
    if (process.env.NODE_ENV === "development") {
      console.warn("Cache cleanup skipped:", error);
    }
  }
};

// Suppress console errors and warnings in production only
if (process.env.NODE_ENV === "production") {
  const originalConsoleError = console.error;
  const originalConsoleWarn = console.warn;

  console.error = (...args) => {
    // Still log critical errors that might need attention
    const message = args[0];
    if (typeof message === 'string' && (
      message.includes('Network Error') ||
      message.includes('Failed to fetch') ||
      message.includes('ChunkLoadError')
    )) {
      // Allow network-related errors to show (they're user-actionable)
      originalConsoleError.apply(console, args);
    }
    // Suppress other console errors in production
  };

  console.warn = () => { }; // Suppress all warnings in production
}

// Initialize i18n without blocking render
(async () => {
  try {
    await initializeI18n();
  } catch (error) {
    console.warn('i18n initialization failed, using defaults:', error);
  }
})();

if (process.env.NODE_ENV === "production") {
  clearStaleBrowserCaches();
}

// Render app immediately
ReactDOM.render(
  <React.StrictMode>
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <BrowserRouter basename="/">
          <HelmetProvider>
            <MyProvider>
              <Suspense fallback={<PageLoader />}>
                <App />
              </Suspense>
            </MyProvider>
          </HelmetProvider>
        </BrowserRouter>
      </PersistGate>
    </Provider>
  </React.StrictMode>,
  document.getElementById("root")
);

reportWebVitals();
